import { Button, Loading } from "@lyfegen-platform/react-common";
import { Box, Card, Typography, useTheme } from "@material-ui/core";
import { Form, Formik, useFormikContext, validateYupSchema, yupToFormErrors } from "formik";
import { Dispatch, Fragment, SetStateAction, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import * as Yup from "yup";
import DeleteForm, { IDeleteFormProps } from "../../containers/data/forms/DeleteForm";
import useStyles from "../../containers/data/forms/forms-styles";
import { dark } from "../../hooks/use-map-theme-styles";
import { FlexContext } from "../../models/flexible/flex-context";
import { FlexSectionController } from "../../models/flexible/flex-section-controller";
import { StatusHistorical } from "../../models/flexible/status-historical";
import { isStartingPoint } from "../../models/flexible/typed-flex-entity";
import { FlexContainerType, FlexEntityFeature, FlexFormStatus } from "../../models/generated/graphql";
import { flexibleFormRelatedAgreements, flexibleFormValuesState, pageLoadingState } from "../../store/flexible";
import { bottomFixedDrawerState } from "../../store/layout";
import { isEmptyValue } from "../../utils/is-empty-value";
import { SectionRoot } from "./SectionRoot";

interface IProps {
	context: FlexContext;
	initialValues: any;
	validationSchema: Yup.AnySchema;
	canSelectNextPage: boolean;
	canSelectPreviousPage: boolean;
	handleNextPage: (values: any) => void;
	handlePreviousPage: (values: any) => void;
	handleEditingModeTransition: (isStart: boolean) => void;
	handleSave: (values: any) => void;
	handleSubmit: (values: any) => void;
	isRecordDrawer?: boolean;
	isDeletable?: boolean;
	setIsNavigateOutAllowed?: Dispatch<SetStateAction<boolean>>;
	patients?: Array<{ patientId: string; minAdministrationDate: string }>;
	loading?: boolean;
}

const setMandatoryAttributeIdentifiers = (section: FlexSectionController, set: Set<string> = new Set<string>()) => {
	section.fieldControllers
		.filter((field) => field.schema.attribute.validation === StatusHistorical.Mandatory)
		.forEach((field) => set.add(field.schema.attribute.identifier));
	section.sectionControllers?.forEach((section) => {
		setMandatoryAttributeIdentifiers(section, set);
	});
	return set;
};

const getMandatoryFieldErrors = (key: string, values: any, errors: any, set: Set<string> = new Set<string>()): Set<string> => {
	if (isEmptyValue(values)) {
		if (typeof errors === "string") {
			set.add(key);
		}
		return set;
	}

	if (Array.isArray(errors)) {
		return errors.reduce(
			(set, errorGroup, i) => getMandatoryFieldErrors(key, (values as Array<any>)[i] as object, errorGroup, set),
			set
		);
	}

	if (typeof errors === "object") {
		return Object.keys(errors).reduce((set, key) => getMandatoryFieldErrors(key, values[key], errors[key], set), set);
	}

	return set;
};

function DrawerEffect(props: IProps) {
	const setDrawerState = useSetRecoilState(bottomFixedDrawerState);
	const { values, isValid, errors, validateForm } = useFormikContext();
	const { t } = useTranslation();
	const { palette, spacing } = useTheme();
	const { push } = useHistory();
	const isForm = props.context.entityType.containerType === FlexContainerType.Form;
	const tableLink = isForm ? "/data/forms" : "/data/records";
	const isFormStartingPoint = isStartingPoint(props.context.flexEntity);
	const deletionProps: IDeleteFormProps = {
		id: props.context.flexEntity?.id,
		isStartingPoint: isFormStartingPoint,
		caseCount: isFormStartingPoint ? props.context.cases.length : 0,
		onDelete: () => {
			props.setIsNavigateOutAllowed && props.setIsNavigateOutAllowed(true);
		},
	};
	const mandatoryAttributeIdentifiers = useMemo(() => {
		const mandatoryAttributeIdentifiers = new Set<string>();
		props.context.sectionControllers.forEach((section) => setMandatoryAttributeIdentifiers(section, mandatoryAttributeIdentifiers));
		return mandatoryAttributeIdentifiers;
	}, [props.context]);

	const mandatoryFieldErrors = useMemo(() => getMandatoryFieldErrors("", values, errors), [errors]);
	const isEmptyMandatoryFields = useMemo(
		() => new Set([...mandatoryAttributeIdentifiers].filter((i) => mandatoryFieldErrors.has(i))).size > 0,
		[mandatoryAttributeIdentifiers, mandatoryFieldErrors]
	);

	useEffect(() => {
		setTimeout(() => validateForm(), 0);
	}, [values]);

	useEffect(() => {
		setDrawerState(
			<Fragment>
				<Box>
					<Button style={{ minWidth: spacing(15) }} color="primary" onClick={() => push(tableLink)}>
						{t("cancel")}
					</Button>
					{props.canSelectPreviousPage && (
						<Button onClick={() => props.handlePreviousPage(values)} style={{ minWidth: spacing(15) }} color="primary">
							{t("back")}
						</Button>
					)}
				</Box>
				<Box display="flex" alignItems="center">
					{props.context.entityType.containerType === FlexContainerType.Form && props.isDeletable && (
						<>
							<DeleteForm {...(deletionProps || {})} isIcon={false} />
						</>
					)}
					{props.canSelectNextPage && (
						<Button
							disabled={!isValid}
							variant="contained"
							color="primary"
							style={{ marginLeft: spacing(4), minWidth: spacing(15) }}
							onClick={() => props.handleNextPage(values)}
						>
							{t("continue").toLocaleUpperCase()}
						</Button>
					)}
					{!props.context.isEditable &&
						props.context.flexEntity?.status === FlexFormStatus.Submitted &&
						!props.canSelectNextPage &&
						props.context.entityType.features.includes(FlexEntityFeature.Editing) && (
							<Button
								variant="outlined"
								color="primary"
								onClick={() => props.handleEditingModeTransition(true)}
								style={{ marginLeft: spacing(4), minWidth: spacing(15) }}
							>
								{t("edit").toLocaleUpperCase()}
							</Button>
						)}
					{props.context.isEditable &&
						props.context.entityType.containerType === FlexContainerType.Form &&
						!props.canSelectNextPage &&
						!props.context.noPatients && (
							<Button
								disabled={
									isEmptyMandatoryFields || (props.context.flexEntity?.status === FlexFormStatus.Submitted && !isValid)
								}
								variant="outlined"
								color="primary"
								onClick={() => props.handleSave(values)}
								style={{ marginLeft: spacing(4), minWidth: spacing(15) }}
							>
								{t("save").toLocaleUpperCase()}
							</Button>
						)}
					{props.context.isEditable && !props.canSelectNextPage && !!props.context.noPatients && (
						<Typography style={{ color: palette.tableText.main }}>
							{" "}
							{t("bulk-coverage-forms-created-placeholder", {
								fromCount: props.context.noPatients || 0,
							})}
						</Typography>
					)}

					{props.context.isEditable &&
						props.context.flexEntity?.status !== FlexFormStatus.Submitted &&
						!props.canSelectNextPage && (
							<Button
								disabled={!isValid || props.loading}
								style={{ marginLeft: spacing(4), minWidth: spacing(15) }}
								variant="contained"
								color="primary"
								onClick={() => props.handleSubmit(values)}
							>
								{props.loading ? <Loading size={20} /> : t("save-and-submit").toLocaleUpperCase()}
							</Button>
						)}
				</Box>
			</Fragment>
		);

		return () => setDrawerState(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.canSelectNextPage, props.canSelectPreviousPage, isValid, values, isEmptyMandatoryFields, props.loading]);

	return null;
}

interface IRevocationFieldsProps {
	predecessor?: string;
	successor?: string;
}

const RevocationFields = (props: IRevocationFieldsProps) => {
	const { t } = useTranslation();
	const styles = useStyles();

	return (
		<Box display="flex" flexWrap="wrap" pl={3}>
			<Box className={styles.colLong} pr={5} paddingY={2.5} pl={1.5}>
				{props.predecessor && (
					<>
						<Typography variant="caption" color="textSecondary">
							{t("predecessor")}
						</Typography>
						<Typography style={{ color: dark, wordBreak: "break-word" }}>{props.predecessor}</Typography>
					</>
				)}
			</Box>
			<Box className={styles.colLong} pr={5} paddingY={2.5}>
				{props.successor && (
					<>
						<Typography variant="caption" color="textSecondary">
							{t("successor")}
						</Typography>
						<Typography style={{ color: dark, wordBreak: "break-word" }}>{props.successor}</Typography>
					</>
				)}
			</Box>
		</Box>
	);
};

export default function FlexView(props: IProps) {
	const styles = useStyles();
	const mergedFormValues = useRecoilValue(flexibleFormValuesState);
	const relatedAgreementsState = useRecoilValue(flexibleFormRelatedAgreements);
	const [pageLoading, setPageLoading] = useRecoilState(pageLoadingState);

	// reset page is loading
	useEffect(() => () => setPageLoading(false), [setPageLoading]);
	const isDeletable = !!props.context.entityType.features?.includes(FlexEntityFeature.Deletion);

	return (
		<Fragment>
			<Box hidden={pageLoading} minWidth={props.context.isEditable ? 1440 : undefined} width="100%" maxWidth="100%" marginBottom={5}>
				<Formik
					initialValues={props.initialValues}
					validate={(values: any) => {
						try {
							validateYupSchema(values, props.validationSchema, true, {
								mergedFormValues,
								values,
								relatedAgreementsState,
							});
						} catch (e) {
							return yupToFormErrors(e);
						}
						return;
					}}
					onSubmit={() => undefined}
					validateOnChange
				>
					<Form>
						{!props.isRecordDrawer && <DrawerEffect {...props} isDeletable={isDeletable} />}
						<Card className={styles.page}>
							<Box mx={3}>
								{props.context.sectionControllers.map((s, si) => (
									<SectionRoot key={si} controller={s} parentPath={""} />
								))}
								{props.isRecordDrawer && (
									<RevocationFields
										predecessor={props.context.flexEntity?.predecessor?.id}
										successor={props.context.flexEntity?.successor?.id}
									/>
								)}
							</Box>
						</Card>
					</Form>
				</Formik>
			</Box>
			{pageLoading && (
				<Box width="100%" height="100%" justifyContent="center" alignItems="center" display="flex" padding="20vh">
					<Loading />
				</Box>
			)}
		</Fragment>
	);
}
